import React, { ReactNode } from 'react';
import styles from './CaseStudyTemplate.module.css';
import { FaQuoteRight, FaTerminal } from 'react-icons/fa';
import ExpertSection from '../sections/Expert/ExpertSection';
import { isSmallScreen } from '../../utils/screen';
import { useTranslation } from '../../hooks/useTranslation';

interface CaseStudyContent {
    id: string;
    title: ReactNode;
    subtitle: ReactNode;
    projectDescription: ReactNode;
    tasks: ReactNode[];
    introImage: string;
    introImageSource: ReactNode;
    introImageSourceUrl: string;
    description: {
        title: ReactNode;
        text: ReactNode[];
        image: string;
        imageSource: ReactNode;
        imageSourceUrl: string;
    };
    technology: {
        title: ReactNode;
        text: ReactNode[];
        image: string;
        imageSource: ReactNode;
        imageSourceUrl: string;
    };
    guarantees: {
        title: ReactNode;
        description: ReactNode;
        items: Array<{
            title: ReactNode;
            description: ReactNode;
        }>;
    };
    recommendations: {
        title: ReactNode;
        description: ReactNode;
        additionalDescription?: ReactNode;
        testimonial: {
            quote: ReactNode[];
            author: {
                name: string;
                role: ReactNode;
                image: string;
            };
        };
    };
    expert: {
        title: ReactNode;
        description: ReactNode;
        quote: ReactNode[];
        author: {
            name: string;
            role: ReactNode;
            image: string;
        };
    };
    education: {
        title: ReactNode;
        description: ReactNode;
        quote: {
            text: ReactNode;
            author: {
                image: string;
            };
        };
    };
    audit: {
        title: ReactNode;
        description: ReactNode;
        additionalDescription: ReactNode;
        type: 'cards' | 'image';
        cards?: Array<{
            title: ReactNode;
            description: ReactNode;
        }>;
        image?: string;
        imageSource?: string;
    };
}

interface CaseStudyTemplateProps {
    content: CaseStudyContent;
}

const CaseStudyTemplate: React.FC<CaseStudyTemplateProps> = ({ content }) => {
    const { t } = useTranslation();
    
    return (
        <div className={styles.template}>
            <section className={styles.intro}>
                <div className={styles.container}>
                    <div className={styles.introContent}>
                        <div className={styles.rowContainer}>
                            <div className={styles.leftContent}>
                                <div className={styles.headerContainer}>
                                    <div className={styles.textContent}>
                                        <h2 className={styles.title}>{content.title}</h2>
                                        <p className={styles.projectDescription}>{content.projectDescription}</p>

                                        <h2 className={styles.tasksTitle}>
                                            {t("caseStudy.template.projectRole")}
                                        </h2>
                                    </div>
                                </div>
                                <div className={styles.tasksGrid}>
                                    {content.tasks.map((task, index) => (
                                        <div key={index} className={styles.taskCard}>
                                            {task}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={styles.imageContent}>
                                <img 
                                    src={content.introImage} 
                                    alt={typeof content.title === 'string' ? content.title : 'Case study image'} 
                                />
                                <p className={styles.imageCaption}>
                                    {t("caseStudy.template.source")} {content.introImageSource}, 
                                    {content.introImageSourceUrl}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className={styles.description}>
                <div className={styles.container}>
                    <div className={styles.descriptionContent}>
                        <div className={styles.descriptionImageWrapper}>
                            <img src={content.description.image} alt={t("alt.projectDescription")} className={styles.descriptionImage} />
                            <p className={styles.imageCaption}>
                                {t("caseStudy.template.source")} {content.description.imageSource},
                                {content.description.imageSourceUrl}
                            </p>
                        </div>
                        <div className={styles.headerContainer}>
                            <FaTerminal size={isSmallScreen() ? 44 : 54} className={styles.terminalIcon} />
                            <div className={styles.textContent}>
                                <h2 className={styles.descriptionTitle}>{content.description.title}</h2>
                                {content.description.text.map((paragraph, index) => (
                                    <p key={index} className={styles.descriptionParagraph}>{paragraph}</p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className={styles.technology}>
                <div className={styles.container}>
                    <div className={styles.technologyContent}>
                        <div className={styles.headerContainer}>
                            <FaTerminal size={isSmallScreen() ? 44 : 54} className={styles.terminalIcon} />
                            <div className={styles.textContent}>
                                <h2 className={styles.technologyTitle}>{content.technology.title}</h2>
                                {content.technology.text.map((paragraph, index) => (
                                    <p key={index} className={styles.technologyParagraph}>{paragraph}</p>
                                ))}
                            </div>
                        </div>
                        <div className={styles.technologyImageWrapper}>
                            <img src={content.technology.image} alt={t("alt.technologyStack")} className={styles.technologyImage} />
                            <p className={styles.imageCaption}>
                                {t("caseStudy.template.source")} {content.technology.imageSource}
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            <ExpertSection
                title={content.expert.title}
                description={content.expert.description}
                quotes={content.expert.quote}
                author={content.expert.author}
            />
            <section className={styles.guarantees}>
                <div className={styles.container}>
                    <div className={styles.guaranteesContent}>
                        <div className={styles.headerContainer}>
                            <FaTerminal size={isSmallScreen() ? 44 : 54} className={styles.terminalIcon} />
                            <div className={styles.textContent}>
                                <h2 className={styles.guaranteesTitle}>{content.guarantees.title}</h2>
                                <p className={styles.guaranteesDescription}>{content.guarantees.description}</p>
                            </div>
                        </div>
                        <div className={styles.guaranteesGrid}>
                            {content.guarantees.items.map((item, index) => (
                                <div key={index} className={styles.guaranteeCard}>
                                    <h3 className={styles.guaranteeTitle}>{item.title}</h3>
                                    <p className={styles.guaranteeDescription}>{item.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
            <section className={styles.recommendations}>
                <div className={styles.container}>
                    <div className={styles.recommendationsContent}>
                        <div className={styles.recommendationsInfo}>
                            <div className={styles.headerContainer}>
                                <FaTerminal size={isSmallScreen() ? 44 : 54} className={styles.terminalIcon} />
                                <div className={styles.textContent}>
                                    <h2 className={styles.recommendationsTitle}>{content.recommendations.title}</h2>
                                    {content.recommendations.description && (
                                        <p className={styles.recommendationsDescription}>{content.recommendations.description}</p>
                                    )}
                                    {content.recommendations.additionalDescription && (
                                        <p className={styles.recommendationsAdditional}>{content.recommendations.additionalDescription}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={styles.recommendationsTestimonial}>
                            <FaQuoteRight className={styles.quoteIcon} size={36} />
                            {content.recommendations.testimonial.quote.map((paragraph, index) => (
                                <p key={index} className={styles.testimonialQuote}>{paragraph}</p>
                            ))}
                            <div className={styles.testimonialAuthor}>
                                <img
                                    src={content.recommendations.testimonial.author.image}
                                    alt={content.recommendations.testimonial.author.name}
                                    className={styles.testimonialAuthorImage}
                                />
                                <div className={styles.testimonialAuthorDetails}>
                                    <h3 className={styles.testimonialAuthorName}>
                                        {content.recommendations.testimonial.author.name}
                                    </h3>
                                    <p className={styles.testimonialAuthorRole}>
                                        {content.recommendations.testimonial.author.role}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className={styles.education}>
                <div className={styles.educationContent}>
                    <div className={styles.educationHeader}>
                        <FaTerminal size={isSmallScreen() ? 44 : 54} className={styles.terminalIcon} />
                        <h2 className={styles.educationTitle}>{content.education.title}</h2>
                        <p className={styles.educationDescription}>{content.education.description}</p>
                    </div>
                    <div className={styles.educationQuoteWrapper}>
                        <div className={styles.educationImage}>
                            {content.education.quote.author.image && (
                                <img
                                    src={content.education.quote.author.image}
                                    alt={t("alt.educationAuthor")}
                                    className={styles.educationAuthorImage}
                                />
                            )}
                        </div>
                        <div className={styles.educationQuote}>
                            <FaQuoteRight className={styles.quoteIcon} size={36} />
                            <p className={styles.quoteText}>{content.education.quote.text}</p>
                        </div>
                    </div>
                </div>
            </section>
            <section className={styles.audit}>
                <div className={styles.container}>
                    <div className={styles.auditContent}>
                        <div className={styles.rowContainer}>
                            <FaTerminal size={isSmallScreen() ? 44 : 54} className={styles.terminalIcon} />
                            <div className={styles.auditInfo}>
                                <h2 className={styles.auditTitle}>{content.audit.title}</h2>
                                <p className={styles.auditDescription}>{content.audit.description}</p>
                                <p className={styles.auditAdditional}>{content.audit.additionalDescription}</p>
                            </div>
                        </div>
                        {content.audit.type === 'cards' ? (
                            <div className={styles.auditCards}>
                                {content.audit.cards?.map((card, index) => (
                                    <div key={index} className={styles.auditCard}>
                                        <h3 className={styles.auditCardTitle}>{card.title}</h3>
                                        <p className={styles.auditCardDescription}>{card.description}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className={styles.auditImageWrapper}>
                                {content.audit.image && (
                                    <img src={content.audit.image} alt={t("alt.audit")} className={styles.auditImage} />
                                )}
                                {(content.audit.imageSource || content.technology.imageSource) && (
                                    <p className={styles.imageCaption}>
                                        {t("caseStudy.template.source")} {content.audit.imageSource || content.technology.imageSource}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CaseStudyTemplate; 